import { displayOf, draftDocOf, draftOf, narrativeOrder, type StoryNode } from '@dispositio/core';
import { de } from '../i18n/de';
import { linkedSince, newIgnored, spannedSince, type GuideEnv, type GuideStep, type GuideTarget } from './tutorialGuide';

/**
 * Führung „Verknüpfungen und Beziehungen“ an der „Schachnovelle“ (Fassung ohne eingebaute Fehler, apps/server/src/services/exampleSchach.ts):
 * In „Die Falle“, die noch unverknüpft ist, verknüpft der Nutzer ein Wort, eine Wendung und ein Vorkommen (der Ort wird Schauplatz),
 * ordnet eine Handlung zu und setzt eine Zeitmarke von Hand; in „Zweihundertfünfzig Dollar“ übernimmt er eine erkannte, relative
 * Zeitangabe, in „Im Hospital“ das Wissen „Erfährt“. In der Bibliothek legt er Beziehungen in eigenen Worten an (Vater von, verfeindet mit
 * … ab …, eine Haltung) und lässt den Donauschiffer sterben – danach steht in „Abfahrt“ rot, dass er am Kai winkt, und der
 * Element-Kontext zeigt ihn als tot. Den Prüfbericht selbst zeigt die „Fehlersuche“.
 */

const T = de.guide.links;
const css = (selector: string): GuideTarget => ({ selector });
const present = (selector: string): boolean => typeof document !== 'undefined' && !!document.querySelector(selector);

// bedienbare Bereiche der Schritte (siehe `allow`)
const VIEW_EDITOR = '[data-testid="view-editor"]';
const VIEW_DESIGN = '[data-testid="view-design"]';
const DESIGN = '[data-testid="design-view"]';
const BINDER_SCENE = 'aside.doc-tree [data-testid="outline-scene"]';
/** eine Szene im Binder öffnen (nur ihr Titel: die Knöpfe der Zeile bleiben gesperrt) */
const BINDER_OPEN = `${BINDER_SCENE} [data-testid="scene-open"]`;
/** die Seite des Manuskripts (nicht die Textfelder der Seitenleiste, die auch .rt-content sind) */
const PAGE = '.rt-content.rt-page';
const LINK_BTN = '[data-testid="rt-link-ref"]';
const BROWSER = '[data-testid="ref-browser"]';
const SUGGEST = ['[data-testid="rt-terms"]', '[data-testid="rt-review"]'];
const TIME_BTN = '[data-testid="rt-time-btn"]';
const TIME_EDITOR = '[data-testid="time-editor"]';
const SIDE = 'aside.side';
const STATEMENT = `${SIDE} [data-testid="statement-input"]`;
const ISSUES_BTN = '[data-testid="open-issues"]';

/** die Szene mit diesem Titel (in Lesereihenfolge) */
const sceneNamed = (env: GuideEnv, title: string): StoryNode | undefined => narrativeOrder(env.ctx).scenes.find((s) => displayOf(env.ctx, s.scene).title === title)?.scene;
/** Im Manuskript ist der Szenentext dieser Szene offen */
const textOpen = (env: GuideEnv, title: string): boolean => {
  const scene = sceneNamed(env, title);
  return env.ws.view === 'editor' && !!scene && env.ws.openDoc?.nodeId === scene.id && env.ws.openDoc.docId === draftDocOf(scene)?.id;
};
/** ihre Zeile im Binder; im Manuskript noch nicht: der Knopf „Manuskript“ */
const binderRow = (env: GuideEnv, title: string): GuideTarget[] => {
  const scene = sceneNamed(env, title);
  return [...(scene ? [css(`${BINDER_SCENE}[data-node-id="${scene.id}"]`)] : []), css(VIEW_EDITOR)];
};
/** Die Szene hat ihre Zeit aus dem Text */
const timeFromText = (env: GuideEnv, title: string): boolean => sceneNamed(env, title)?.time?.source === 'text';
const entityOf = (env: GuideEnv, key: string) => env.ctx.story.entities.find((x) => x.refKey === key);
/** In der Seitenleiste ist diese Entität offen */
const entityOpen = (env: GuideEnv, key: string): boolean => env.ws.selection?.kind === 'entity' && env.ws.selection.id === entityOf(env, key)?.id;
/** Auswahl für `enter.select`: diese Entität (zeigt die Seitenleiste auch nach dem Neuladen) */
const pick = (key: string) => (env: GuideEnv) => {
  const id = entityOf(env, key)?.id;
  return id ? ({ kind: 'entity', id } as const) : null;
};
const card = (env: GuideEnv, key: string): GuideTarget[] => [css(`[data-testid="entity-card"][data-entity-id="${entityOf(env, key)?.id ?? ''}"]`), css(DESIGN)];
/** Beziehung dieser Art, die von der Entität ausgeht */
const relationOf = (env: GuideEnv, key: string, kind: string) => entityOf(env, key)?.relations?.find((r) => r.kind === kind);
/** Der Donauschiffer ist in einer Szene gestorben */
const fatherDead = (env: GuideEnv): boolean => {
  const id = entityOf(env, 'vater')?.id;
  return !!id && env.ctx.story.nodes.some((n) => (n.changes ?? []).some((c) => c.entityId === id && c.kind === 'verfassung' && c.value === 'tot'));
};
const linkedKey = (env: GuideEnv, step: string, key: string): boolean => linkedSince(env, step).includes(key);
/** In „Im Hospital“ steht ein Bezug „Erfährt“ */
const learned = (env: GuideEnv): boolean => {
  const scene = sceneNamed(env, 'Im Hospital');
  return !!scene && /\{@[^}]*\berfährt\}/u.test(draftOf(scene));
};

export const LINK_STEPS: GuideStep[] = [
  { id: 'vk-start', text: () => T.start, targets: () => [css('[data-testid="viewbar"]')] },
  { id: 'vk-open', text: () => T.open, targets: (e) => binderRow(e, 'Die Falle'), until: (e) => textOpen(e, 'Die Falle'), allow: [VIEW_EDITOR, BINDER_OPEN] },
  { id: 'vk-plain', text: () => T.plain, targets: () => [css(PAGE)], also: () => [[css('[data-testid="rt-terms"]')]], dock: 'side' },
  // Verknüpfen: ein Wort, eine Wendung, ein Vorkommen
  { id: 'vk-name', text: () => T.name, targets: () => [css(LINK_BTN)], remember: true, until: (e) => linkedKey(e, 'vk-name', 'mcconnor'), dock: 'side', allow: [PAGE, LINK_BTN, BROWSER, ...SUGGEST] },
  { id: 'vk-phrase', text: () => T.phrase, targets: () => [css(LINK_BTN)], remember: true, until: (e) => linkedKey(e, 'vk-phrase', 'frau'), dock: 'side', allow: [PAGE, LINK_BTN, BROWSER] },
  {
    id: 'vk-term',
    text: () => T.term,
    targets: () => [css(`${PAGE} .rt-term[data-term="Smoking Room"]`), css('[data-testid="rt-terms"]')],
    remember: true,
    until: (e) => linkedKey(e, 'vk-term', 'rauchsalon') || newIgnored(e, 'vk-term'),
    dock: 'side',
    allow: [PAGE, BROWSER, ...SUGGEST],
  },
  {
    id: 'vk-objects',
    text: () => T.objects,
    targets: () => [css(['mcconnor', 'frau', 'rauchsalon'].map((k) => `${SIDE} [data-testid="scene-object"][data-ref="${k}"]`).join(', ')), css(SIDE)],
    enter: { sceneTab: 'objects' },
    place: 'left',
  },
  // Bezug: eine Stelle gehört zu McConnor
  { id: 'vk-span', text: () => T.span, targets: () => [css(LINK_BTN)], remember: true, until: (e) => spannedSince(e, 'vk-span').length > 0, dock: 'side', allow: [PAGE, LINK_BTN, BROWSER] },
  { id: 'vk-spanDone', text: () => T.spanDone, targets: () => [css('[data-testid="span-label"]')], dock: 'side' },
  // Zeit im Text: von Hand, dann ein erkannter, relativer Vorschlag
  { id: 'vk-timeMark', text: () => T.timeMark, targets: () => [css(TIME_BTN)], until: (e) => timeFromText(e, 'Die Falle'), dock: 'side', allow: [PAGE, TIME_BTN, TIME_EDITOR] },
  { id: 'vk-timeDone', text: () => T.timeDone, targets: () => [css(`${PAGE} .rt-time`), css(PAGE)], dock: 'side' },
  { id: 'vk-openDollar', text: () => T.openDollar, targets: (e) => binderRow(e, 'Zweihundertfünfzig Dollar'), until: (e) => textOpen(e, 'Zweihundertfünfzig Dollar'), allow: [BINDER_OPEN] },
  {
    id: 'vk-timeSuggest',
    text: () => T.timeSuggest,
    targets: () => [css(`${PAGE} .rt-time-suggest`), css(PAGE)],
    until: (e) => timeFromText(e, 'Zweihundertfünfzig Dollar'),
    dock: 'side',
    allow: [PAGE, ...SUGGEST],
  },
  { id: 'vk-timeRel', text: () => T.timeRel, targets: () => [css(`${PAGE} .rt-time`), css(PAGE)], dock: 'side' },
  // Wissen: Dr. B. erfährt von der Schachvergiftung
  { id: 'vk-openHospital', text: () => T.openHospital, targets: (e) => binderRow(e, 'Im Hospital'), until: (e) => textOpen(e, 'Im Hospital'), allow: [BINDER_OPEN] },
  { id: 'vk-learn', text: () => T.learn, targets: () => [css(LINK_BTN)], until: learned, dock: 'side', allow: [PAGE, LINK_BTN, BROWSER] },
  { id: 'vk-learnDone', text: () => T.learnDone, targets: () => [css('[data-testid="span-label"]'), css(PAGE)], also: () => [[css(ISSUES_BTN)]], dock: 'side' },
  // Beziehungen und Tod in der Bibliothek: der Donauschiffer, dann Dr. B.
  { id: 'vk-toLibrary', text: () => T.toLibrary, targets: () => [css(VIEW_DESIGN)], until: (e) => e.ws.view === 'design', allow: [VIEW_DESIGN] },
  { id: 'vk-openFather', text: () => T.openFather, targets: (e) => card(e, 'vater'), until: (e) => entityOpen(e, 'vater'), enter: { designFocus: 'person' }, allow: [DESIGN] },
  { id: 'vk-family', place: 'left', text: () => T.family, targets: () => [css(STATEMENT)], until: (e) => !!relationOf(e, 'vater', 'elternteil'), enter: { select: pick('vater'), entityTab: 'history' }, allow: [SIDE] },
  { id: 'vk-familyDone', place: 'left', text: () => T.familyDone, targets: () => [css(`${SIDE} [data-testid="relation-row"]`), css(SIDE)], enter: { select: pick('vater'), entityTab: 'history' } },
  { id: 'vk-death', place: 'left', text: () => T.death, targets: () => [css(STATEMENT)], until: fatherDead, enter: { select: pick('vater'), entityTab: 'history' }, allow: [SIDE] },
  { id: 'vk-deathDone', place: 'left', text: () => T.deathDone, targets: () => [css(`${SIDE} [data-testid="state-life"]`), css(SIDE)], enter: { select: pick('vater'), entityTab: 'history' } },
  { id: 'vk-openDrb', text: () => T.openDrb, targets: (e) => card(e, 'drb'), until: (e) => entityOpen(e, 'drb'), allow: [DESIGN] },
  { id: 'vk-enemy', place: 'left', text: () => T.enemy, targets: () => [css(STATEMENT)], until: (e) => !!relationOf(e, 'drb', 'feindschaft'), enter: { select: pick('drb'), entityTab: 'history' }, allow: [SIDE] },
  {
    id: 'vk-attitude',
    place: 'left',
    text: () => T.attitude,
    targets: () => [css(`${SIDE} [data-testid="relation-row"][data-kind="feindschaft"] [data-testid="relation-attitude"]`), css(SIDE)],
    until: (e) => !!relationOf(e, 'drb', 'feindschaft')?.attitude?.source,
    enter: { select: pick('drb'), entityTab: 'history' },
    allow: [SIDE],
  },
  { id: 'vk-attitudeDone', place: 'left', text: () => T.attitudeDone, targets: () => [css(`${SIDE} [data-testid="relation-row"][data-kind="feindschaft"]`), css(SIDE)], enter: { select: pick('drb'), entityTab: 'history' } },
  // die Folge des Todes: der Widerspruch in „Abfahrt“ und der Stand am verknüpften Namen
  { id: 'vk-toAbfahrt', text: () => T.toAbfahrt, targets: (e) => binderRow(e, 'Abfahrt'), until: (e) => textOpen(e, 'Abfahrt'), allow: [VIEW_EDITOR, BINDER_OPEN] },
  { id: 'vk-deadText', text: () => T.deadText, targets: () => [css(`${PAGE} .rt-conflict`), css(PAGE)], dock: 'side', allow: [PAGE] },
  { id: 'vk-glance', text: () => T.glance, targets: () => [css(`${PAGE} .rt-ref-link[data-ref="vater"]`), css(PAGE)], unlock: () => present('[data-testid="ref-glance"]'), dock: 'side', allow: [PAGE] },
  { id: 'vk-end', text: () => T.end, free: true },
];
